// Utility to generate PDF HTML for ELISA Builder results
// EXACT copy of Angular's results_pdf.html template structure

// Import base64 images
import imageBase64 from "../image-base64.json";

export function generatePdfHtml(results, getString) {
  // Get catalog domain for links - handle local, QA, and production environments
  let environment_url;
  if (
    window.location.hostname.includes("localhost") ||
    window.location.hostname.includes("127.0.0.1")
  ) {
    // Local development - use production domain
    environment_url = "https://www.thermofisher.com";
  } else if (window.location.hostname.includes("thermofisher")) {
    // ThermoFisher domain (QA or production) - use current protocol and host
    environment_url = `${window.location.protocol}//${window.location.hostname}`;
  } else {
    // Other environments - default to production
    environment_url = "https://www.thermofisher.com";
  }

  // Collect search/button cards for footer
  const cards = [];

  // Helper to escape HTML entities (not used in Angular but keeping for safety)
  const escapeHtml = (text) => {
    if (!text) return "";
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  };

  // Helper to get translation strings - matches Angular's getString function
  // Angular calls getString("results."+key) in the helper, so keys should be like "pdf.title"
  const getStr = (key) => {
    if (getString) {
      // Add results. prefix if not already present
      const fullKey = key.startsWith("results.") ? key : `results.${key}`;
      const result = getString(fullKey);

      // Check if translation was found (not returning the key itself or [KEY])
      if (
        result &&
        result !== fullKey &&
        !result.startsWith("[") &&
        !result.endsWith("]")
      ) {
        return result;
      }
    }

    // Fallback translations matching Angular strings
    const translations = {
      "pdf.title": "Our recommendations",
      "pdf.tool_name": "ELISA Builder",
      "pdf.intro":
        "<strong>Based on your answers</strong>, we have provided a list of products and reagents that are best suited for your ELISA needs.",
      "pdf.table.product": "Product",
      "pdf.table.category": "Category",
      "pdf.table.unit_size": "Unit size",
      "pdf.table.price": "Price",
      "pdf.table.catalog_no": "Catalog number",
      "pdf.page": "Page",
      "pdf.page_of": "of",
      "group.best-solution": "Recommended Products",
      "group.additional-products": "Additional Products",
      "pdf.card.matched-antibody-pairs": "Matched Antibody Pairs",
      "pdf.card.matched-antibody-pairs.link":
        "/elisa/query/filter/assaykit/matched+pair?persistFilters=yes",
      "pdf.card.primary-antibodies": "Primary Antibodies",
      "pdf.card.primary-antibodies.link":
        "/antibody/primary/filter/application/ELISA",
      "pdf.card.secondary-antibodies": "Secondary Antibodies",
      "pdf.card.secondary-antibodies.link": "/antibody/secondary/",
      "pdf.card.recombinant-proteins": "Recombinant Proteins",
      "pdf.card.recombinant-proteins.link":
        "/proteins/query/filter/application/ELISA?persistFilters=yes",
      "pdf.card.btn": "View products",
    };
    return translations[key] || key;
  };

  // Image helper - Load base64 images from JSON file (like Angular's gulp-generated images)
  const getImage = (name) => {
    // Return base64 image from imported JSON file, or empty fallback
    return imageBase64[name] || imageBase64.empty_png || "";
  };

  // Start HTML document - Simplified structure that works with server-side PDF generator
  let html = `<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
	<meta charset="UTF-8"/>
	<title>${getStr("pdf.title")}</title>
	<style type="text/css">
        @page {
            size: letter portrait;
            margin: 1cm 0;
        }

        body {
            font-family: "Helvetica Neue", "Helvetica", "Arial", "sans-serif";
            font-weight: 300;
            background-color: #fff;
            color: #333333;
            font-size: 10pt;
            margin: 0;
            padding: 0;
        }
        .page-wrapper {
            width: 100%;
            padding: 0 1cm;
            box-sizing: border-box;
        }

        .page-header {
            width: 100%;
            padding: 1cm 0;
            box-sizing: border-box;
        }

        .page-header img {
            float: left;
            width:15%;
        }

        .page-header .title {
            float: right;
            font-size: 16pt;
            font-weight: 300;
            padding-top: 10px;
        }

        .summary {
            background: #f7f7f7;
            padding: 1cm;
            border-top: 1px solid #ddd;
            border-bottom: 1px solid #ddd;
        }

        .summary h1 {
            font-size: 16pt;
            margin-bottom: 5mm;
            font-weight: bold;
        }

        .summary p {
            margin: 0;
            font-size: 10pt;
        }

        h2 {
            font-size: 14pt;
            margin: 15px 0 15px 0;
            font-weight: 400;
            border-bottom: 1px solid #ddd;
            padding-bottom: 5px;
        }

        .additional-products-section {
            margin:0 1cm;
            page-break-before: always;
        }

        table.product-table {
            border-collapse: collapse;
            width: 100%;
            table-layout: auto;
            padding: 0;
            margin: 0 0 3mm;
            font-size: 8pt;
        }

        table.product-table th {
            border-bottom: 2px solid #ddd;
            padding: 8px 5px;
            text-align: left;
            font-weight: 600;
        }

        table.product-table td {
            border-bottom: 1px solid #e5e5e5;
            padding: 10px 5px;
            vertical-align: top;
        }

        .product-cell {
            display: table;
            width: 100%;
        }

        .product-img {
            display: table-cell;
            width: 60px;
            vertical-align: top;
            padding-right: 10px;
        }

        .product-img img {
            max-width: 60px;
            max-height: 60px;
            width: 60px;
            height: 60px;
            object-fit: contain;
        }

        .product-info {
            display: table-cell;
            vertical-align: top;
        }

        .product-brand {
            font-weight: 600;
            display: block;
            margin-bottom: 3px;
        }

        .product-name {
            color: #0066cc;
            text-decoration: none;
        }

        .footer-cards {
            margin-top: 40px;
            page-break-before: always;
        }

        .footer-cards .page-header {
            margin-bottom: 30px;
        }

        .card-container {
            display: table;
            width: 100%;
            border-collapse: separate;
            border-spacing: 10px 0;
        }

        .card {
            display: table-cell;
            width: 23%;
            border: 1px solid #ddd;
            padding: 15px 10px;
            text-align: center;
            background: #fff;
            vertical-align: top;
        }

        .card-icon {
            display: block;
            width: 100%;
            text-align: center;
            margin-bottom: 15px;
        }

        .card-icon img {
            display: inline-block;
            width: 45px;
            height: 45px;
        }        .card-title {
            font-weight: 600;
            font-size: 10pt;
            margin-bottom: 10px;
        }

        .card-button {
            display: inline-block;
            padding: 6px 12px;
            border: 1px solid #0066cc;
            color: #0066cc;
            text-decoration: none;
            font-size: 9pt;
            margin-bottom: 8px;
        }

        .card-url {
            font-size: 7pt;
            color: #666;
            word-break: break-all;
        }
	</style>
</head>
<body>

<div class="page-wrapper">
  <div class="page-header">
    <img width="140" src="${getImage(
      "logo_color_png"
    )}" alt="ThermoFisher Scientific"/>
    <div class="title">${getStr("pdf.tool_name")}</div>
    <div style="clear: both;"></div>
  </div>
</div>

<div class="summary">
    <div style="float: right; padding-left: 20px;">
        <img src="${getImage(
          "banner_png"
        )}" alt="ELISA Kits" style="width: 200px; height: auto;" />
    </div>
    <h1>${getStr("pdf.title")}</h1>
    <p>${getStr("pdf.intro")}</p>
    <div style="clear: both;"></div>
</div>

`;
  results.groups.forEach((group, idx) => {
    if (group.items && group.items.length > 0) {
      // Add page break and header for additional products section
      if (group.key === "additional-products") {
        html += `
   
<div class="additional-products-section">
    <div class="page-header">
        <img width="140" src="${getImage(
          "logo_color_png"
        )}" alt="ThermoFisher Scientific"/>
        <div class="title">${getStr("pdf.tool_name")}</div>
        <div style="clear: both;"></div>
    </div>
    <h2>${getStr("group.additional-products")}</h2>
</div>`;
      }

      html += `
      <div class="page-wrapper">
<table class="product-table">
    <thead>
        <tr>
            <th style="width: 35%">${getStr("pdf.table.product")}</th>
            <th style="width: 15%">${getStr("pdf.table.category")}</th>
            <th style="width: 15%">${getStr("pdf.table.unit_size")}</th>
            <th style="width: 20%">${getStr("pdf.table.price")}</th>
            <th style="width: 15%">${getStr("pdf.table.catalog_no")}</th>
        </tr>
    </thead>
    <tbody>`;

      group.items.forEach((item) => {
        if (item.type === "search" || item.type === "button") {
          cards.push(item);
          return;
        }

        if (!item.product) {
          return;
        }

        const product = item.product;
        let price = "";
        if (product.price && product.price.trim() !== "") {
          price = product.price;
        } else {
          price = "Not orderable online";
        }

        // Robust image URL logic: always fallback to placeholder if missing/invalid
        let productImageSrc = getImage("empty_png"); // Default fallback
        let imageUrl = (product.image && product.image.trim()) || "";

        if (imageUrl && imageUrl.length > 4) {
          // must be a non-trivial string
          // Add -250.jpg if not present and not base64
          if (!imageUrl.startsWith("data:") && !imageUrl.endsWith("-250.jpg")) {
            imageUrl = imageUrl + "-250.jpg";
          }
          if (imageUrl.startsWith("data:")) {
            productImageSrc = imageUrl;
          } else if (
            imageUrl.startsWith("http://") ||
            imageUrl.startsWith("https://")
          ) {
            productImageSrc = imageUrl;
          } else if (imageUrl.startsWith("//")) {
            productImageSrc = `https:${imageUrl}`;
          } else if (imageUrl.startsWith("/")) {
            productImageSrc = `${window.location.protocol}//${window.location.host}${imageUrl}`;
          } else if (
            imageUrl.includes("thermofisher.com") ||
            imageUrl.includes("assets.thermofisher.com")
          ) {
            productImageSrc = imageUrl.startsWith("http")
              ? imageUrl
              : `https://${imageUrl}`;
          } else {
            productImageSrc = `https://assets.thermofisher.com/TFS-Assets/LSG/product-images/${imageUrl}`;
          }
        }

        html += `
        <tr>
            <td>
                <div class="product-cell">
                    <div class="product-img">
                        <img src="${escapeHtml(
                          productImageSrc
                        )}" alt="${escapeHtml(
          product.name || "Product image"
        )}"/>
                    </div>
                    <div class="product-info">
                        <span class="product-brand">Thermo Scientific™</span>
                        <a class="product-name" href="${escapeHtml(
                          product.url || "#"
                        )}">${product.name || ""}</a>
                    </div>
                </div>
            </td>
            <td>${escapeHtml(product.label || item.category || "")}</td>
            <td>${escapeHtml(product.size || "Each")}</td>
            <td>${price}</td>
            <td>${escapeHtml(product.sku || "")}</td>
        </tr>`;
      });

      html += `
    </tbody>
</table>
</div>`;
    }
  });

  // Add footer cards section on new page
  if (cards.length > 0 || true) {
    // Always show cards section
    html += `
<div class="page-wrapper">
  <div class="footer-cards">
      <div class="card-container">
          <div class="card">
              <div class="card-icon">
                  <img src="${getImage(
                    "card_icon_matched_antibody_pairs"
                  )}" alt="Matched Antibody Pairs"/>
              </div>
              <div class="card-title">Matched Antibody Pairs</div>
              <a class="card-button" href="${environment_url}/elisa/query/filter/assaykit/matched+pair?persistFilters=yes">View products</a>
              <div class="card-url">${environment_url}/elisa/query/filter/assaykit/matched+pair?persistFilters=yes</div>
          </div>
          
          <div class="card">
              <div class="card-icon">
                  <img src="${getImage(
                    "card_icon_primary_antibodies"
                  )}" alt="Primary Antibodies"/>
              </div>
              <div class="card-title">Primary Antibodies</div>
              <a class="card-button" href="${environment_url}/antibody/primary/filter/application/ELISA">View products</a>
              <div class="card-url">${environment_url}/antibody/primary/filter/application/ELISA</div>
          </div>
          
          <div class="card">
              <div class="card-icon">
                  <img src="${getImage(
                    "card_icon_secondary_antibodies"
                  )}" alt="Secondary Antibodies"/>
              </div>
              <div class="card-title">Secondary Antibodies</div>
              <a class="card-button" href="${environment_url}/antibody/secondary/">View products</a>
              <div class="card-url">${environment_url}/antibody/secondary/</div>
          </div>
          
          <div class="card">
              <div class="card-icon">
                  <img src="${getImage(
                    "card_icon_recombinant_proteins"
                  )}" alt="Recombinant Proteins"/>
              </div>
              <div class="card-title">Recombinant Proteins</div>
              <a class="card-button" href="${environment_url}/proteins/query/filter/application/ELISA?persistFilters=yes">View products</a>
              <div class="card-url">${environment_url}/proteins/query/filter/application/ELISA?persistFilters=yes</div>
          </div>
      </div>
  </div>
</div>`;
  }

  html += `

</body>
</html>`;
  return html;
}
